"use client";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@templates/design-system";
import AutoScroll from "embla-carousel-auto-scroll";
import { useEffect, useRef, useState } from "react";
import { getComponent } from "../../../../client";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import type { ILogoCloudCarouselProps } from "./LogoCloudCarousel.props";

export const LogoCloudCarousel = ({ items }: ILogoCloudCarouselProps) => {
  const Link = getComponent("Link");
  const autoScroll = useRef(
    AutoScroll({
      direction: "backward",
      playOnInit: true,
      speed: 1.5,
      startDelay: 0,
      stopOnFocusIn: true,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );
  const repeatedItems = [
    ...items.map((item) => ({ hidden: false, item })),
    ...items.map((item) => ({ hidden: true, item })),
  ];
  const [api, setApi] = useState<CarouselApi>();

  // The marquee is decoration, so it costs nothing when it cannot be seen: it never
  // starts for a visitor who asked for reduced motion, and it pauses off-screen
  // instead of driving a transform for the whole session.
  useEffect(() => {
    if (!api) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      autoScroll.current.stop();
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? autoScroll.current.play() : autoScroll.current.stop()),
      { rootMargin: "120px" },
    );
    observer.observe(api.rootNode());
    return () => observer.disconnect();
  }, [api]);

  return (
    <Carousel
      aria-label="Affiliated organizations"
      className="signal-marquee-mask overflow-hidden border-y border-border"
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) autoScroll.current.play();
      }}
      onFocusCapture={() => autoScroll.current.stop()}
      opts={{ align: "start", dragFree: true, loop: true }}
      plugins={[autoScroll.current]}
      setApi={setApi}
      tabIndex={0}
    >
      <CarouselContent className="-ml-10" viewportClassName="py-8">
        {repeatedItems.map(({ hidden, item }, index) => {
          const logo = (
            <DaylightMedia
              className="h-8 w-40 bg-transparent"
              framed={false}
              imageClassName="object-contain invert mix-blend-screen opacity-45 transition-opacity duration-200 group-hover:opacity-100"
              media={item.logo}
              sizes="144px"
            />
          );
          return (
            <CarouselItem
              aria-hidden={hidden || undefined}
              className="basis-auto pl-10"
              key={`${hidden ? "repeat" : "authored"}-${index}`}
            >
              {item.logoLink && !hidden ? (
                <Link
                  aria-label={item.logoLink.accessibilityLabel || undefined}
                  className="group block focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={item.logoLink.href}
                  rel={item.logoLink.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.logoLink.openInNewTab ? "_blank" : undefined}
                >
                  {logo}
                </Link>
              ) : (
                logo
              )}
            </CarouselItem>
          );
        })}
      </CarouselContent>
    </Carousel>
  );
};

LogoCloudCarousel.displayName = "Content::LogoCloudCarousel";
